// This owner is intentionally ported from the legacy JSX provider; its runtime
// contract is covered by the moved auth suites while gradual type cleanup is
// outside this ownership transfer.
// @ts-nocheck
import PropTypes from 'prop-types';
import { useCallback, useRef, useState, useEffect, useLayoutEffect } from 'react';
import client from '../../../api/client';
import { readinessApi } from '../../../api/readinessApi';
import { clearNextClientCache, registerTokenRefresher } from '../api/userKgClient';
import { invalidateRequestCache } from '../api/requestCache';
import { isAccessPendingError, routeToAccessPending } from '../../../lib/accessPendingRedirect';
import { MOCK_AUTH_TOKEN, shouldSeedMockAuthToken } from '../../../lib/authMode';
import {
    clearAuthoritativeAuthIdentity,
    setAuthoritativeAuthIdentity,
} from '../../../lib/authIdentity';
import { clearChooseRoleSuppression } from '../family/familyAccountHome';
import { clearSessionOwnedDrafts } from '../../../lib/sessionCleanup';
import {
    clearAuthVerificationState,
    markAuthVerificationFailed,
    markAuthVerificationPending,
} from '../../../lib/authVerificationState';
import {
    buildSessionExpiredLoginPath,
    expireProductSession,
    resolveSessionExpiryDestination,
    SESSION_EXPIRED_EVENT,
} from '../../../lib/sessionExpiry';
import { authContextValue as AuthContext } from './authContextValue';
import { verifyWithRetry } from './verifyWithRetry';
import { applyAuthenticatedSentryContext } from '../../../config/sentry';
import { useCanonicalProductPath } from '../navigation/ProductNavigation';


export const AuthProvider = ({ children }) => {
    const canonicalProductPath = useCanonicalProductPath();
    // OPTIMISTIC INIT: If we have a token, assume logged in immediately.
    // This removes the "white screen" or "spinner" delay on page load.
    const [user, setUser] = useState(() => {
        // The Sentry scope is process-global. Reset it synchronously while
        // creating this provider's initial unverified state so a remount cannot
        // expose a prior account's class to a child layout effect.
        applyAuthenticatedSentryContext(null);
        if (shouldSeedMockAuthToken()) {
            const mockUser = {
                id: 'e2e-user',
                email: 'e2e@example.com',
                name: 'E2E User',
                family_account_type: 'student',
                family_parent_onboarding_version: 1,
                token: MOCK_AUTH_TOKEN
            };
            setAuthoritativeAuthIdentity(mockUser, MOCK_AUTH_TOKEN);
            return mockUser;
        }
        const t = localStorage.getItem('access_token');
        clearAuthoritativeAuthIdentity();
        if (t) markAuthVerificationPending();
        return t ? { token: t, name: 'Loading...' } : null;
    });

    // Set the tag before publishing the matching profile. This prevents a
    // child layout/error boundary from inheriting the preceding account's tag
    // during the render that first exposes a verified identity. Objects without
    // the server-derived whitelist value, and null on logout, reset to unknown.
    const setAuthenticatedUser = useCallback((nextUser) => {
        applyAuthenticatedSentryContext(nextUser);
        setUser(nextUser);
    }, []);

    const [token, setToken] = useState(localStorage.getItem('access_token'));

    // Loading is only true if we are waiting for something critical (which we aren't anymore)
    const [loading, setLoading] = useState(false);
    // Set only by the backend's structured 403 access_pending decision. A
    // stored token alone is never sufficient evidence to show redemption UI.
    const [accessPending, setAccessPending] = useState(false);
    // A stored token remains a valid session candidate when the profile API is
    // temporarily unavailable. Keep that state distinct from authentication
    // failure so protected routes can show honest recovery without signing out.
    const [profileUnavailable, setProfileUnavailable] = useState(false);

    // True while the INITIAL gate/profile verification (/api/users/me) is still in flight
    // for a stored token. ProtectedRoute waits on this so a not-yet-allowlisted user is
    // sent to /access-pending WITHOUT first flashing the gated content. We start true only
    // when we boot with a token but no verified profile yet (the optimistic case).
    const [verifying, setVerifying] = useState(
        () => !shouldSeedMockAuthToken() && !!localStorage.getItem('access_token')
    );

    // Single-verification-owner attempt (plan-single-verifier-owner.md §2.1
    // plus ENG-R1-001 residual). One object `{ token, controller }` in a
    // single ref. null = the boot effect (Owner A) owns verification — the
    // safe, idle state. Ownership is object identity (`ref.current ===
    // attempt`), not token equality: a cached Auth0 token can be reused
    // after logout or by a superseding same-token attempt, and the previous
    // attempt's `finally` must not clear the newer owner. Starting a new
    // attempt aborts the previous controller and replaces the ref. Decays
    // to null on logout and cannot survive a reload (in-memory only).
    const interactiveVerificationAttemptRef = useRef(null);

    const stillOwnsInteractiveVerification = (attempt) =>
        interactiveVerificationAttemptRef.current === attempt
        && !attempt.controller.signal.aborted;

    const claimInteractiveVerification = (token) => {
        const previous = interactiveVerificationAttemptRef.current;
        if (previous?.controller && !previous.controller.signal.aborted) {
            previous.controller.abort();
        }
        const attempt = { token, controller: new AbortController() };
        interactiveVerificationAttemptRef.current = attempt;
        return attempt;
    };

    const releaseInteractiveVerification = (attempt) => {
        if (interactiveVerificationAttemptRef.current === attempt) {
            interactiveVerificationAttemptRef.current = null;
        }
    };

    const logout = useCallback(() => {
        const currentAttempt = interactiveVerificationAttemptRef.current;
        interactiveVerificationAttemptRef.current = null;
        if (currentAttempt?.controller && !currentAttempt.controller.signal.aborted) {
            currentAttempt.controller.abort();
        }
        clearSessionOwnedDrafts();
        clearChooseRoleSuppression();
        // Clear all possible token keys for safety
        localStorage.removeItem('auth_token');
        localStorage.removeItem('access_token');
        // Prevent cross-session BLEED: a returning or different user must not
        // inherit the previous user's program selection, cached v2 data, or the
        // silent-refresh hook. (logout previously cleared only the token + state,
        // so the v2 in-memory caches + last_program leaked into the next login.)
        localStorage.removeItem('last_program');
        localStorage.removeItem('user');
        clearAuthoritativeAuthIdentity();
        clearAuthVerificationState();
        // Detach request ownership immediately: a response from the signed-out
        // account may still resolve, but it cannot populate cache for the next
        // account. clearNextClientCache also clears the v2 module/SWR stores.
        try { invalidateRequestCache(); } catch { /* noop */ }
        try { clearNextClientCache(); } catch { /* noop */ }
        // One-shot flag: the first Insights read after the NEXT login passes
        // ?refresh=1 to bust the server-side v2_insights cache.
        try { localStorage.setItem('pp_insights_refresh', '1'); } catch { /* noop */ }
        try { registerTokenRefresher(null); } catch { /* noop */ }
        setToken(null);
        setAuthenticatedUser(null);
        setAccessPending(false);
        setProfileUnavailable(false);
        setVerifying(false);
    }, [setAuthenticatedUser]);

    useLayoutEffect(() => {
        const handleSessionExpired = (event) => {
            const returnTo = resolveSessionExpiryDestination(event?.detail?.returnTo);
            logout();
            window.location.assign(canonicalProductPath(buildSessionExpiredLoginPath(returnTo)));
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    }, [canonicalProductPath, logout]);

    // Abort the interactive verification (if any) when the provider unmounts.
    // Owner A's own effect aborts its own controller in its own cleanup below;
    // this covers the interactive-owner controller, which is not tied to that
    // effect's lifecycle.
    useEffect(() => () => {
        const currentAttempt = interactiveVerificationAttemptRef.current;
        if (currentAttempt?.controller && !currentAttempt.controller.signal.aborted) {
            currentAttempt.controller.abort();
        }
        interactiveVerificationAttemptRef.current = null;
    }, []);

    useEffect(() => {
        let cancelled = false;
        const verificationController = new AbortController();

        if (shouldSeedMockAuthToken()) {
            localStorage.setItem('access_token', MOCK_AUTH_TOKEN);
            setAuthoritativeAuthIdentity(user, MOCK_AUTH_TOKEN);
            clearAuthVerificationState();
            return;
        }

        // Restore User from Token
        if (token) {
            // If we already have full user profile, skip (unless force refresh needed)
            if (user && user.email) {
                clearAuthVerificationState();
                return;
            }
            // The interactive path (loginWithEmail / handleExternalLogin) owns
            // verification for this exact token — do not re-verify it here.
            if (interactiveVerificationAttemptRef.current?.token === token) {
                return;
            }
            markAuthVerificationPending();

            // Verify with backend to get user info (Silent Background Fetch)
            const verificationToken = token;
            verifyWithRetry((signal) => client.get('/api/users/me', {
                signal,
                skipAccessPendingRedirect: true,
                headers: { Authorization: `Bearer ${verificationToken}` },
            }), undefined, verificationController.signal)
                .then(response => {
                    if (cancelled) return;
                    // Preserve the token in the user object
                    const verifiedUser = { ...response.data, token };
                    setAuthoritativeAuthIdentity(verifiedUser, token);
                    setAuthenticatedUser(verifiedUser);
                    setAccessPending(false);
                    setProfileUnavailable(false);
                    clearAuthVerificationState();
                    setVerifying(false);

                    // Trigger Readiness Warmup (Fire-and-forget)
                    if (verifiedUser.family_account_type !== 'guardian') {
                        readinessApi.triggerWarmup();
                    }
                })
                .catch(error => {
                    if (cancelled) return;
                    console.error('Failed to verify token:', error);
                    // R16: a 403 access_pending means the token is VALID but the
                    // user isn't allowlisted — route to /access-pending, don't
                    // logout (logging out would drop the valid session). Keep
                    // Initiate the redirect before settling verification so the
                    // gated route cannot render, then settle it so a second boot
                    // already at /access-pending can render the pending screen.
                    if (isAccessPendingError(error)) {
                        clearAuthVerificationState();
                        setAccessPending(true);
                        setProfileUnavailable(false);
                        routeToAccessPending({
                            nextSource: 'current-page',
                            candidate: `${window.location.pathname}${window.location.search}${window.location.hash || ''}`,
                        });
                        setVerifying(false);
                        return;
                    }
                    if (window.navigator.onLine === false) {
                        const offlinePath = canonicalProductPath('/offline');
                        markAuthVerificationFailed();
                        setAccessPending(false);
                        setProfileUnavailable(false);
                        if (window.location.pathname !== offlinePath) {
                            window.location.assign(offlinePath);
                        }
                        setVerifying(false);
                        return;
                    }
                    // A stored-token 401 is a real expiry transition: clear the
                    // protected tree and preserve only a reviewed Product return.
                    if (error.response?.status === 401) {
                        expireProductSession(undefined, token);
                        setVerifying(false);
                        return;
                    }
                    // A generic 403 is invalid but is not described as expiry.
                    if (error.response?.status === 403) {
                        logout();
                    }
                    // Verification settled (non-gate error / network): stop blocking
                    // so ProtectedRoute can show an explicit verification failure.
                    markAuthVerificationFailed();
                    setAccessPending(false);
                    setProfileUnavailable(true);
                    setVerifying(false);
                });
        } else {
            // If user state exists but no token, clear it
            if (user) setAuthenticatedUser(null);
            clearAuthoritativeAuthIdentity();
            clearAuthVerificationState();
            setAccessPending(false);
            setProfileUnavailable(false);
            setVerifying(false);
        }

        return () => {
            cancelled = true;
            verificationController.abort();
        };
    }, [canonicalProductPath, logout, setAuthenticatedUser, token, user]);

    // (a5) Re-entrancy guard — closes R6. `disabled={isLoggingIn}` protects
    // the button, but state is async and cannot guard a synchronous double
    // submit (e.g. two fireEvent.submit calls in the same tick). While a
    // login this function owns is in flight, return that SAME promise rather
    // than issuing a second POST /api/token grant.
    const loginInFlightRef = useRef(null);

    const loginWithEmail = (email, password, returnTo) => {
        if (loginInFlightRef.current) {
            return loginInFlightRef.current;
        }
        const attempt = performLoginWithEmail(email, password, returnTo).finally(() => {
            loginInFlightRef.current = null;
        });
        loginInFlightRef.current = attempt;
        return attempt;
    };

    const performLoginWithEmail = async (email, password, returnTo) => {
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        let access_token;
        try {
            const response = await client.post('/api/token', formData);
            access_token = response.data.access_token;
        } catch (error) {
            // Bad credentials (401) etc. — no token issued.
            console.error("Login failed", error);
            return handleAuthError(error, "Login failed", returnTo);
        }

        // Persist the token BEFORE verifying. Valid creds always yield a token now —
        // even an inactive (access_pending) user — so they keep a session to redeem
        // with after being routed to /access-pending. Verify may 403 (gate); that's
        // expected for inactive users and is handled below.
        localStorage.setItem('access_token', access_token);
        clearChooseRoleSuppression();
        clearAuthoritativeAuthIdentity();
        // (a1) Do NOT setToken here — that would expose an intermediate
        // token-set render before verification settles, which is the seam
        // that lets Owner A (the boot effect) re-verify the same token
        // (plan-single-verifier-owner.md §2.1 a1). `token` is committed in the
        // same synchronous block as `user`/`accessPending`/`profileUnavailable`
        // below, in every branch. (a2) Claim this attempt before any state
        // write so Owner A's early-return guard sees the token immediately
        // if it re-runs. Claiming aborts any previous attempt and replaces
        // the ref — ownership is object identity, not token equality.
        const attempt = claimInteractiveVerification(access_token);
        let releaseOwnership = true;

        try {
            const userData = await verifyToken(access_token, attempt.controller.signal);
            // logout() (or unmount) may have aborted and cleared identity while
            // this request was in flight, OR a NEWER interactive attempt (a
            // different loginWithEmail call this same guard already
            // serializes, or a concurrent handleExternalLogin — ENG-R1-001)
            // may have replaced this attempt object. A late success must not
            // resurrect a signed-out user or a superseding attempt's
            // identity — ownership is `ref.current === attempt`.
            if (!stillOwnsInteractiveVerification(attempt)) {
                return { success: false, error: '' };
            }
            const verifiedUser = { ...userData, token: access_token };
            setAuthoritativeAuthIdentity(verifiedUser, access_token);
            setToken(access_token);
            setAuthenticatedUser(verifiedUser);
            setAccessPending(false);
            setProfileUnavailable(false);
            clearAuthVerificationState();
            if (verifiedUser.family_account_type !== 'guardian') {
                readinessApi.triggerWarmup();
            }
            return { success: true, user: verifiedUser };
        } catch (error) {
            if (!stillOwnsInteractiveVerification(attempt)) {
                return { success: false, error: '' };
            }
            // Token is valid but the gate may 403 access_pending → route to
            // /access-pending (token already persisted). Keep the session.
            if (isAccessPendingError(error)) {
                // Keep ownership (do not release in finally): `setToken` below
                // changes Owner A's effect dependency in THIS same tick, before
                // React has run the re-render that would observe a cleared
                // ref. Releasing here reopens the exact window R4 describes —
                // Owner A would fire its own verification for this token and
                // could route to /access-pending a second time. `user` never
                // gets an email on this branch, so the `user && user.email`
                // guard cannot cover it the way it does on the success branch.
                // Safe: this is a real document navigation (`routeToAccessPending`
                // -> window.location.assign), so the page is about to reload,
                // which is one of the decay guarantees (§2.1 a2 point 4).
                releaseOwnership = false;
                clearAuthVerificationState();
                setToken(access_token);
                setAccessPending(true);
                setProfileUnavailable(false);
                routeToAccessPending({ nextSource: 'login-query', candidate: returnTo });
                return { success: false, error: '', accessPending: true };
            }
            if (error.response?.status === 401 || error.response?.status === 403) {
                // A generic profile-authority 401/403 invalidates the newly
                // issued session. Only the structured access_pending 403 above
                // proves a valid gated session. Transport/5xx failures are not
                // evidence that the password grant was revoked.
                localStorage.removeItem('access_token');
                clearAuthoritativeAuthIdentity();
                setToken(null);
                setAuthenticatedUser(null);
                setAccessPending(false);
                setProfileUnavailable(false);
                clearAuthVerificationState();
                console.error("Login failed", error);
                return handleAuthError(error, "Login failed");
            }

            // Transient failure (5xx / offline): the password grant is still
            // good, so keep the session and route to recovery rather than
            // discarding a valid token over a network blip.
            // (a2) Deliberate exception to the finally release below: keep
            // ownership of this token so the boot effect does not fire an
            // unrequested second verification the instant the recovery
            // screen renders (§2.1 a2 point 3's decay guarantees are (2)
            // token-scope, (4) reload, and (5) logout — not this branch).
            releaseOwnership = false;
            const recoveryPath = window.navigator.onLine === false ? '/offline' : '/unavailable';
            setToken(access_token);
            setAuthenticatedUser({ token: access_token, name: 'Loading...' });
            setAccessPending(false);
            setProfileUnavailable(recoveryPath === '/unavailable');
            markAuthVerificationFailed();
            setVerifying(false);
            console.error("Profile verification unavailable after login", error);
            return { success: false, error: '', recoveryPath };
        } finally {
            // Clear the ref only if it still identifies THIS attempt object.
            if (releaseOwnership) {
                releaseInteractiveVerification(attempt);
            }
        }
    };

    /**
     * R16 — three-way auth error branch. Order matters: detect the structured
     * 403 access_pending dict FIRST, then fall back to string rendering.
     *
     *   (a) HTTP 401              -> render `detail` as an inline string error.
     *   (b) HTTP 403 + detail.code === "access_pending"
     *                             -> stash the current ?next= into
     *                                the timestamped consume-once Product safe return and
     *                                route to /access-pending. Returns no error
     *                                string (we are navigating away).
     *   (c) any other 403         -> render detail.message ?? "Access denied".
     *
     * Never render a dict as text — that is the bug this replaces (the old code
     * returned error.response?.data?.detail raw, which stringifies an object to
     * "[object Object]").
     */
    const handleAuthError = (error, fallback, returnTo) => {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;

        // (b) Access-pending gate — structured 403 dict. Check first.
        if (isAccessPendingError(error)) {
            clearAuthVerificationState();
            setAccessPending(true);
            routeToAccessPending({ nextSource: 'login-query', candidate: returnTo });
            return { success: false, error: '', accessPending: true };
        }

        // (a) 401 — bad credentials / not authenticated. Inline string.
        if (status === 401) {
            const msg = typeof detail === 'string' ? detail : (fallback || 'Not authenticated');
            return { success: false, error: msg };
        }

        // (c) any other 403 — render a message, never a dict.
        if (status === 403) {
            const msg = (detail && typeof detail === 'object')
                ? (detail.message ?? 'Access denied')
                : (typeof detail === 'string' ? detail : 'Access denied');
            return { success: false, error: msg };
        }


        // Other errors — best-effort string.
        const msg = typeof detail === 'string' ? detail : (fallback || 'Login failed');
        return { success: false, error: msg };
    };

    const handleExternalLogin = async (token, returnTo) => {
        clearChooseRoleSuppression();
        clearAuthoritativeAuthIdentity();
        // (a2) Same attempt-object ownership as loginWithEmail. Owner A's
        // early-return guard is `attemptRef.current?.token === token`.
        // Setting `user` to `{ token }` (no email) below does NOT make
        // Owner A's `user && user.email` skip fire; without this claim,
        // Owner A still re-verifies (§1.3). Claiming aborts any previous
        // attempt so same-token supersession cannot let A commit.
        const attempt = claimInteractiveVerification(token);
        localStorage.setItem('access_token', token);
        setToken(token);
        setAuthenticatedUser({ token });

        try {
            // Return a promise that resolves when user is set or rejects
            return await new Promise((resolve, reject) => {
                verifyToken(token, attempt.controller.signal).then(u => {
                    // Abort (logout/unmount) or supersession by a NEWER
                    // interactive attempt (loginWithEmail or another
                    // handleExternalLogin call — this path has no
                    // re-entrancy guard) must refuse this late write.
                    // Ownership is `ref.current === attempt`.
                    if (!stillOwnsInteractiveVerification(attempt)) {
                        resolve(u);
                        return;
                    }
                    const verifiedUser = { ...u, token };
                    setAuthoritativeAuthIdentity(verifiedUser, token);
                    setAuthenticatedUser(verifiedUser);
                    setAccessPending(false);
                    setProfileUnavailable(false);
                    clearAuthVerificationState();
                    setLoading(false);

                    // Trigger Readiness Warmup (Fire-and-forget)
                    if (verifiedUser.family_account_type !== 'guardian') {
                        readinessApi.triggerWarmup();
                    }

                    resolve(u);
                }).catch(e => {
                    if (!stillOwnsInteractiveVerification(attempt)) {
                        reject(e);
                        return;
                    }
                    console.error('Failed to verify external token:', e);
                    // R16: a 403 access_pending is a valid session that isn't yet
                    // allowlisted — route to /access-pending, keep the token.
                    if (isAccessPendingError(e)) {
                        clearAuthVerificationState();
                        setAccessPending(true);
                        setProfileUnavailable(false);
                        routeToAccessPending({ nextSource: 'current-page', candidate: returnTo });
                        setLoading(false);
                        reject(e);
                        return;
                    }
                    // Only logout if 401/403
                    if (e.response && (e.response.status === 401 || e.response.status === 403)) {
                        logout();
                    }
                    setLoading(false);
                    reject(e);
                });
            });
        } finally {
            releaseInteractiveVerification(attempt);
        }
    };

    // (a4) Retry parity — required, not optional. Without this, removing the
    // duplicate verifier would be a regression: today a 502/503/504 during
    // interactive login is retried by Owner A only. Route the interactive
    // verification through the SAME verifyWithRetry helper Owner A uses, so a
    // transient profile-service blip no longer surfaces "temporarily
    // unavailable" while the boot effect's retry silently signs the user in
    // (plan-single-verifier-owner.md §2.4).
    const verifyToken = async (tokenToVerify, signal) => {
        const response = await verifyWithRetry((retrySignal) => client.get('/api/users/me', {
            signal: retrySignal,
            skipAccessPendingRedirect: true,
            headers: { Authorization: `Bearer ${tokenToVerify}` },
        }), undefined, signal);
        return response.data;
    };

    // Re-fetches the internal user profile without disturbing loading/verifying
    // state. Used after a write that changes server-side profile fields the
    // client needs immediately (e.g. establishing family_account_type on
    // /choose-role) so gates that read `user.family_account_type` see the new
    // value without a full page reload. Never call this speculatively: on
    // failure it leaves `user` untouched rather than logging out, since a
    // transient refresh failure is not proof the session is invalid.
    const refreshUser = useCallback(async () => {
        if (!token) return null;
        try {
            const response = await client.get('/api/users/me');
            const refreshedUser = { ...response.data, token };
            setAuthoritativeAuthIdentity(refreshedUser, token);
            setAuthenticatedUser(refreshedUser);
            return refreshedUser;
        } catch (error) {
            console.error('Failed to refresh user profile:', error);
            return null;
        }
    }, [setAuthenticatedUser, token]);

    return (
        <AuthContext.Provider value={{ user, loginWithEmail, logout, loading, verifying, accessPending, profileUnavailable, handleExternalLogin, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
